import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import type { ListVariantTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import type { PagerInfiniteScrollTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import { FormInputFieldInputType } from '@aurora/shared-client/components/form/enums';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import type { FormSpec } from '@aurora/shared-client/components/form/types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import FormBuilder from '@aurora/shared-client/helpers/form/FormBuilder/FormBuilder';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { Form, UserConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { ItemType, UserViewSize, UserViewVariant } from '../../../../types/enums';
import type {
  UserViewFragment,
  UserViewsQuery,
  UserViewsQueryVariables
} from '../../../../types/graphql-types';
import PaneledItemList from '../../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps } from '../../../entities/types';
import userViewsQuery from '../../../users/UserViews.query.graphql';
import useTranslation from '../../../useTranslation';
import localStyles from './SearchUserFilter.module.pcss';
import formSchema from './SearchUserFilterForm.form.json';

interface UserFilterProps {
  /**
   * Name of the filter
   */
  name: string;
  /**
   * Callback function when an item in a menu is selected.
   *
   * @callback
   */
  onChange: () => void;
}

interface UserSearch {
  userName: string;
}

/**
 * This filter enables us to fetch search results by userId
 *
 * @author Akash Goenka
 */

const SearchUserFilter: React.FC<React.PropsWithChildren<UserFilterProps>> = ({
  name,
  onChange
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.SEARCH_USER_FILTER);
  const { formatMessage, loading: textLoading } = i18n;
  const userSuggestionsList = useRef(null);
  const userListItemClassName = 'lia-user-list-item';
  const highlightedItemCss = cx('lia-user-list-item-highlight');
  const [showUserDropDown, setShowUserDropDown] = useState<boolean>(false);
  const [showUserSuggestionsList, setShowUserSuggestionsList] = useState<boolean>(true);
  const { router } = useEndUserRoutes();
  const pageSize = 15;
  const constraints: UserConstraints = { login: { matches: searchTerm } };

  const queryResult = useQueryWithTracing<UserViewsQuery, UserViewsQueryVariables>(
    module,
    userViewsQuery,
    {
      variables: {
        constraints,
        first: pageSize,
        useAvatar: true,
        useRank: true
      },
      skip: searchTerm.length <= 2
    }
  );

  useEffect(() => {
    if (showUserSuggestionsList && userSuggestionsList.current) {
      const users = userSuggestionsList.current.querySelectorAll(`li.${cx(userListItemClassName)}`);
      users[0]?.classList.add(highlightedItemCss);
    }
  }, [cx, highlightedItemCss, queryResult.loading, showUserSuggestionsList]);

  /**
   * This function is used to collapse the user filter and hide suggestions list and reset
   * the input value to empty
   */
  function collapseFilter() {
    setShowUserDropDown(false);
    setShowUserSuggestionsList(false);
    setSearchTerm('');
  }

  /**
   * This function helps to filter search results by user
   * @param userId the id of the selected user
   */
  async function handleUserSelection(userId: string) {
    onChange();
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        ...router.query,
        [EndUserQueryParams.SEARCH_FILTER_BY_AUTHOR]: userId
      },
      { shallow: true }
    );
  }

  /**
   * This function handles the onBlur event so that the user filter can
   * be collapsed when focus shifts outside the dropwdown
   */
  const handleBlur = useCallback(() => {
    if (showUserDropDown) {
      collapseFilter();
    }
  }, [showUserDropDown]);

  /**
   * @returns The li element that is highlighted in the user suggestions list
   */
  function getActiveLineItem() {
    return userSuggestionsList.current.querySelector(`.${highlightedItemCss}`);
  }

  /**
   * This function highlights a li element that has been navigated to via the keyboard
   * @param items The array of li elements in the user suggestions list
   * @param index Index of the li element to be highlighted
   */
  const highlightElement = (items: Array<HTMLElement>, index: number) => {
    if (!(index > items.length - 1)) {
      items[index].classList.add(highlightedItemCss);
      items[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  };

  /**
   * This function is used to remove the highlight from a li element
   * @param items The array of li elements in the user suggestions list
   * @param index Index of the li element on which the highlight is to be removed
   */
  const removeElementHighlight = (items: Array<HTMLElement>, index: number) => {
    if (!(index < 0)) {
      items[index].classList.remove(highlightedItemCss);
    }
  };

  /**
   * This function handles the click action on the user list
   * @param item the selected user object
   */
  async function handleSelect(item: UserViewFragment) {
    await handleUserSelection(item.uid.toString());
    collapseFilter();
  }

  /**
   * This function handles the keyboard navigation on the user suggestions list
   * @param offset This a number that adds to the index from
   * the currently highlighted li element
   */
  const setNextActiveElement = (offset: number) => {
    if (!userSuggestionsList.current) {
      return;
    }
    const items = [
      ...userSuggestionsList.current.querySelectorAll(`li.${cx(userListItemClassName)}`)
    ];
    const activeElement = getActiveLineItem();
    const previousIndex = items.indexOf(activeElement);
    const possibleNextIndex = previousIndex + offset;
    const nextIndex = possibleNextIndex >= items.length ? 0 : possibleNextIndex;
    if (nextIndex < 0) {
      highlightElement(items, items.length - 1);
      removeElementHighlight(items, previousIndex);
      return;
    }
    highlightElement(items, nextIndex);
    removeElementHighlight(items, previousIndex);
  };

  /**
   * This functions handles the enter key action
   * @param event The HTML keyboard event
   */
  const handleKeyBoardSelect = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (
      userSuggestionsList.current &&
      userSuggestionsList.current?.querySelectorAll(`li.${cx(userListItemClassName)}`)?.length > 0
    ) {
      const clickEvent = new Event('mousedown', { bubbles: true, cancelable: false });
      getActiveLineItem().dispatchEvent(clickEvent);
      collapseFilter();
    }
  };

  useEffect(() => {
    const element = document.querySelector('#UserSearchForm-userName') as HTMLElement;
    if (element && showUserDropDown) {
      element.focus();
    }
  }, [showUserDropDown]);

  /**
   * This function handles the key press behaviour on using the eyboard to navigate
   * through the user suggestions list
   * @param event The HTML keydown event
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setNextActiveElement(1);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        setNextActiveElement(-1);
        break;
      }
      case 'Enter': {
        handleKeyBoardSelect(event);
        break;
      }
      case 'Escape': {
        collapseFilter();
        break;
      }
      default: {
        break;
      }
    }
  };

  if (textLoading) {
    return null;
  }

  const viewVariant: ItemViewTypeAndProps<ItemType.USER, UserViewVariant.INLINE> = {
    type: UserViewVariant.INLINE,
    props: {
      useAvatar: true,
      useRank: true,
      useLink: false,
      useJoinDate: false,
      viewSize: UserViewSize.SM
    }
  };

  const pagerVariant: PagerInfiniteScrollTypeAndProps = {
    type: PagerVariant.INFINITE_SCROLL,
    props: { rootReference: userSuggestionsList }
  };

  /**
   * The no result section to be rendered for the PaneledItemList.
   */
  const noResults: React.FC<React.PropsWithChildren<unknown>> = () => (
    <EmptyState
      className={cx('lia-g-py-20')}
      description={formatMessage('emptyDescription')}
      alignment={TextAlignment.CENTER}
    />
  );

  const listVariant: ListVariantTypeAndProps<UserViewFragment> = {
    type: ListVariant.UNSTYLED,
    props: {
      listItemClassName: () => cx(`lia-g-mb-0 ${userListItemClassName}`),
      onItemClick: handleSelect
    }
  };

  const formSpec: FormSpec<UserSearch> = new FormBuilder<UserSearch>('SearchUserFilterForm', i18n, {
    schema: formSchema as Form,
    cx
  })
    .addInputField({
      defaultValue: searchTerm,
      name: 'userName',
      formGroupSpec: {
        label: false
      },
      attributes: {
        autoComplete: 'off'
      },
      focus: true,
      inputType: FormInputFieldInputType.TEXT,
      onKeyDown: handleKeyDown,
      ariaLabel: formatMessage('SearchUserFilterForm.userName.placeholder'),
      onBlur: handleBlur,
      isVisible: {
        watchFields: null,
        callback: () => showUserDropDown
      }
    })
    .build();

  function renderUserTextField(): React.ReactElement {
    return (
      <InputEditForm<UserSearch>
        formSpec={formSpec}
        onSubmit={(formData): Promise<void> => {
          const { userName } = formData;
          if (userName?.length > 2) {
            setSearchTerm(userName);
            setShowUserSuggestionsList(true);
          } else {
            setShowUserSuggestionsList(false);
          }
          return null;
        }}
        submitOnChange={{
          watchFields: ['userName']
        }}
      />
    );
  }

  return (
    <Dropdown show={showUserDropDown}>
      <Dropdown.Toggle
        as={Button}
        variant={ButtonVariant.LINK}
        size="lg"
        className={cx('lia-g-dropdown-toggle')}
        onClick={() => {
          setShowUserDropDown(!showUserDropDown);
        }}
        data-testid="SearchUserFilter.UserFilterToggle"
        aria-label={formatMessage('filterBy', { name: name })}
        data-menu-open={showUserDropDown}
      >
        {name || formatMessage('name')}
      </Dropdown.Toggle>
      <Dropdown.Menu
        popperConfig={dropdownPopperConfig}
        className={cx(showUserDropDown && 'lia-user-filter-menu')}
        data-testid="SearchUserFilter.UserFilterMenu"
      >
        {renderUserTextField()}
        {showUserSuggestionsList && (
          <div className={cx('lia-user-filter-list')} ref={userSuggestionsList} role="log">
            <PaneledItemList<
              UserViewFragment,
              ItemType.USER,
              ItemViewTypeAndProps<ItemType.USER, UserViewVariant>,
              UserViewsQuery,
              UserViewsQueryVariables
            >
              type={ItemType.USER}
              variant={viewVariant}
              queryResult={queryResult}
              listVariant={listVariant}
              pagerVariant={pagerVariant}
              pageSize={pageSize}
              loadingSize={null}
              empty={noResults}
              panel={PanelType.NONE}
              useEmpty
            />
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchUserFilter;
